import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';
import { getAuthHeaders } from '../utils/auth';
import '../assets/styles/StreakRewardSettings.css';

const StreakRewardSettings = ({ householdId }) => {
    const [rewards, setRewards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);

    // Form state for new reward
    const [newReward, setNewReward] = useState({
        days_milestone: '',
        reward_amount: '',
        enabled: true
    });

    const fetchRewards = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/v1/streak-rewards?household_id=${householdId}`, {
                headers: getAuthHeaders()
            });
            setRewards(response.data || []);
            setError('');
        } catch (err) {
            console.error('Failed to fetch streak rewards:', err);
            setError('Failed to load streak rewards');
        } finally {
            setLoading(false);
        }
    }, [householdId]);

    useEffect(() => {
        fetchRewards();
    }, [fetchRewards]);

    const handleCreateReward = async (e) => {
        e.preventDefault();

        if (!newReward.days_milestone || !newReward.reward_amount) {
            setError('Please fill in all fields');
            return;
        }

        if (parseInt(newReward.days_milestone) <= 0) {
            setError('Days must be positive');
            return;
        }

        if (parseInt(newReward.reward_amount) < 0) {
            setError('Reward amount cannot be negative');
            return;
        }

        try {
            await axios.post('/api/v1/streak-rewards', {
                household_id: householdId,
                days_milestone: parseInt(newReward.days_milestone),
                reward_amount: parseInt(newReward.reward_amount),
                enabled: newReward.enabled
            }, {
                headers: getAuthHeaders()
            });

            setNewReward({ days_milestone: '', reward_amount: '', enabled: true });
            setShowAddForm(false);
            fetchRewards();
            setError('');
        } catch (err) {
            console.error('Failed to create streak reward:', err);
            setError(err.response?.data || 'Failed to create streak reward');
        }
    };

    const handleUpdateReward = async (rewardId, updates) => {
        try {
            await axios.put('/api/v1/streak-rewards', {
                reward_id: rewardId,
                ...updates
            }, {
                headers: getAuthHeaders()
            });
            fetchRewards();
            setEditingId(null);
            setError('');
        } catch (err) {
            console.error('Failed to update streak reward:', err);
            setError('Failed to update streak reward');
        }
    };

    const handleDeleteReward = async (rewardId) => {
        if (!window.confirm('Are you sure you want to delete this streak reward?')) {
            return;
        }

        try {
            await axios.delete(`/api/v1/streak-rewards?reward_id=${rewardId}`, {
                headers: getAuthHeaders()
            });
            fetchRewards();
            setError('');
        } catch (err) {
            console.error('Failed to delete streak reward:', err);
            setError('Failed to delete streak reward');
        }
    };

    const toggleEnabled = async (reward) => {
        await handleUpdateReward(reward.id, { enabled: !reward.enabled });
    };

    if (loading) {
        return <div className="streak-rewards-loading">Loading streak rewards...</div>;
    }

    return (
        <div className="streak-rewards-container">
            <div className="streak-rewards-header">
                <h2>🔥 Streak Rewards</h2>
                <p className="streak-rewards-description">
                    Set rewards for kids who maintain daily activity streaks.
                    Kids earn these bonuses automatically when they reach the milestone.
                </p>
            </div>

            {error && <div className="streak-rewards-error">{error}</div>}

            <div className="streak-rewards-list">
                {rewards.length === 0 ? (
                    <div className="streak-rewards-empty">
                        <p>No streak rewards configured yet.</p>
                        <p>Add your first milestone to encourage daily engagement!</p>
                    </div>
                ) : (
                    rewards.map(reward => (
                        <div key={reward.id} className={`streak-reward-card ${!reward.enabled ? 'disabled' : ''}`}>
                            <div className="streak-reward-milestone">
                                <span className="streak-days">{reward.days_milestone}</span>
                                <span className="streak-label">day{reward.days_milestone !== 1 ? 's' : ''}</span>
                            </div>

                            <div className="streak-reward-amount">
                                <span className="reward-value">{reward.reward_amount}</span>
                                <span className="reward-currency">DB</span>
                            </div>

                            <div className="streak-reward-actions">
                                <button
                                    className={`btn-toggle ${reward.enabled ? 'enabled' : 'disabled'}`}
                                    onClick={() => toggleEnabled(reward)}
                                    title={reward.enabled ? 'Disable' : 'Enable'}
                                >
                                    {reward.enabled ? '✓ Enabled' : '✗ Disabled'}
                                </button>

                                <button
                                    className="btn-edit"
                                    onClick={() => setEditingId(reward.id)}
                                >
                                    ✏️ Edit
                                </button>

                                <button
                                    className="btn-delete"
                                    onClick={() => handleDeleteReward(reward.id)}
                                >
                                    🗑️ Delete
                                </button>
                            </div>

                            {editingId === reward.id && (
                                <div className="streak-reward-edit-form">
                                    <input
                                        type="number"
                                        placeholder="Reward Amount"
                                        defaultValue={reward.reward_amount}
                                        onBlur={(e) => {
                                            if (e.target.value && parseInt(e.target.value) !== reward.reward_amount) {
                                                handleUpdateReward(reward.id, {
                                                    reward_amount: parseInt(e.target.value)
                                                });
                                            }
                                        }}
                                    />
                                    <button onClick={() => setEditingId(null)}>Done</button>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {!showAddForm ? (
                <button
                    className="btn-add-reward"
                    onClick={() => setShowAddForm(true)}
                >
                    + Add Streak Milestone
                </button>
            ) : (
                <form className="streak-reward-form" onSubmit={handleCreateReward}>
                    <h3>Add New Streak Milestone</h3>

                    <div className="form-group">
                        <label>Days:</label>
                        <input
                            type="number"
                            min="1"
                            value={newReward.days_milestone}
                            onChange={(e) => setNewReward({ ...newReward, days_milestone: e.target.value })}
                            placeholder="e.g., 7, 30, 90"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Reward Amount (DB):</label>
                        <input
                            type="number"
                            min="0"
                            value={newReward.reward_amount}
                            onChange={(e) => setNewReward({ ...newReward, reward_amount: e.target.value })}
                            placeholder="e.g., 50, 250, 1000"
                            required
                        />
                    </div>

                    <div className="form-group-checkbox">
                        <label>
                            <input
                                type="checkbox"
                                checked={newReward.enabled}
                                onChange={(e) => setNewReward({ ...newReward, enabled: e.target.checked })}
                            />
                            Enabled
                        </label>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="btn-submit">Create Milestone</button>
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={() => {
                                setShowAddForm(false);
                                setNewReward({ days_milestone: '', reward_amount: '', enabled: true });
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            <div className="streak-rewards-info">
                <h4>💡 Tips:</h4>
                <ul>
                    <li>Common milestones: 7 days (week), 30 days (month), 90 days (quarter), 365 days (year)</li>
                    <li>Rewards are automatically awarded when kids reach the milestone</li>
                    <li>Kids see their current streak with a 🔥 icon on their dashboard</li>
                    <li>Streaks reset if a kid doesn't complete any tasks for a day</li>
                </ul>
            </div>
        </div>
    );
};

export default StreakRewardSettings;
